import React, { useState, useEffect } from 'react';
import { Button } from "components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card"
import { Input } from "components/ui/input"
import { Label } from "components/ui/label"
import { auth } from 'firebase-config';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [oobCode, setOobCode] = useState('');

  useEffect(() => {
    // Get oobCode from URL parameters
    const code = searchParams.get('oobCode');
    if (code) {
      setOobCode(code);
      verifyResetCode(code);
    } else {
      setError('reset link non valido. Lancia una nuova richiesta di reset password.');
      setVerifying(false);
    }
  }, [searchParams]);

  const verifyResetCode = async (code: string) => {
    try {
      await verifyPasswordResetCode(auth, code);
      setVerifying(false);
    } catch (err: any) {
      console.error('Reset code verification failed:', err);
      if (err.code === 'auth/invalid-action-code') {
        setError('Questo reset link U+00E8 scaduto o non valido. Lancia una nuova richiesta di reset password.');
      } else {
        setError('Verifica reset link. Riprova');
      }
      setVerifying(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password || !confirmPassword) {
      setError('Per favore inserisci tutti i campi');
      return;
    }

    if (password.length < 6) {
      setError('La password deve contenere almeno 6 caretteri');
      return;
    }

    if (password !== confirmPassword) {
      setError('Le passwords non coincidono');
      return;
    }

    setLoading(true);
    
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset fallito:', err);
      if (err.code === 'auth/invalid-action-code') {
        setError('Questo reset link U+00E8 scaduto. Lancia una nuova richiesta di reset password.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password troppo debole. Inserisci una password migliore.');
      } else {
        setError('Errore durante il reset password. Per favore riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleResetPassword(e as any);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Verifying reset link...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Success!
              </CardTitle>
            </div>
            
            <CardDescription className="text-gray-600 text-base">
              Your password has been successfully reset!
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg">
              <p>You can now sign in with your new password.</p>
            </div>
          </CardContent>
          
          <CardFooter className="pt-0">
            <div className="w-full">
              <Link to="/login">
                <Button className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02]">
                  Ritorna al log in
                </Button>
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center pb-6">
          <div className="flex items-center justify-start mb-4">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <span className="text-sm text-gray-600 ml-2">Back to Login</span>
          </div>
          
          <div className="mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl text-white">🔐</span>
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Reset Password
            </CardTitle>
          </div>
          
          <CardDescription className="text-gray-600 text-base">
            Inserisci la nuova password qui sotto
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Nuova Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Inserisci la nuova password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="h-12 border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                Conferma nuova Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="h-12 border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
              />
            </div>

            <Button 
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Resetting Password...</span>
                </div>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Ricordi la tua password?{" "}
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Accedi
              </Link>
            </p>
          </div>
        </CardContent>
        
        <CardFooter className="pt-0">
          <div className="w-full p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-xs text-orange-800 text-center">
              <strong>Nota di sicurezza:</strong> Il reset link scade in 1 ore per ragioni di sicurezza.
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
} 